import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { BeatLoader } from "react-spinners";
import OtpInput from "react18-input-otp";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { api as auth } from "../../../api";

const GENERIC_CREDENTIALS_ERROR = "Invalid email or password";
const GENERIC_SERVER_ERROR = "Something went wrong. Please try again.";

const LoginForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const initialValues = {
    password: "",
    email: "",
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string().required("Password is required"),
    email: Yup.string()
      .email("Error! invalid email address")
      .required("Please enter your email address"),
  });

  const completeLogin = (session) => {
    toast.success("Logged in Successfully!");
    sessionStorage.setItem("user", JSON.stringify(session));
    if (session?.orgProfileCompletionStatus || session?.role?.slug !== "client-admin") {
      navigate("/admin/dashboard");
    } else {
      navigate("/account-setup");
    }
  };

  const requestOtp = async ({ email: emailValue, password: passwordValue }, formikHelpers) => {
    formikHelpers?.setSubmitting(true);
    try {
      const response = await auth.loginAdmin({
        email: emailValue,
        password: passwordValue,
      });
      setEmail(response.email);
      setPassword(passwordValue);
      setOtp("");
      setStep("otp");
      toast.success(response.message || "A verification code has been sent to your email address");
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 400 && (message === "Invalid email address" || message === "Invalid password")) {
        toast.error(GENERIC_CREDENTIALS_ERROR);
      } else if (status >= 500) {
        toast.error(GENERIC_SERVER_ERROR);
      } else {
        toast.error(message || GENERIC_CREDENTIALS_ERROR);
      }
    } finally {
      formikHelpers?.setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    await requestOtp({ email, password });
    setIsResending(false);
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setIsVerifying(true);
    try {
      const session = await auth.verifyAdminMfa({ email, otp });
      completeLogin(session);
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (
        message === "No pending verification for this account. Please log in again." ||
        message === "Verification code has expired. Please log in again."
      ) {
        toast.error(message);
        setStep("credentials");
        setPassword("");
        setOtp("");
      } else if (status >= 500) {
        toast.error(GENERIC_SERVER_ERROR);
      } else {
        toast.error(message || "Invalid verification code");
        setOtp("");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  if (step === "otp") {
    return (
      <div className="w-full grid place-content-center bg-white">
        <div className="flex flex-col w-full overflow-hidden">
          <div className="mb-6">
            <p className="text-gray600 text-[28px] sm:text-3xl font-bold mb-2">
              Enter verification code
            </p>
            <p className="text-gray400 font-medium text-sm">
              We sent a 6-digit code to <span className="font-semibold">{email}</span>. It expires in 5 minutes.
            </p>
          </div>

          <form onSubmit={handleVerify}>
            <div className="mb-6 flex justify-center">
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                isInputNum
                shouldAutoFocus
                separator={<span className="w-2" />}
                inputStyle={{
                  width: "2.75rem",
                  height: "3rem",
                  fontSize: "1.25rem",
                  borderRadius: "8px",
                  border: "1px solid #E2E6ED",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={isVerifying}
              className="bg-primaryBlue hover:opacity-50 text-white w-full mb-4 rounded-md cursor-pointer font-bold py-3 px-6"
            >
              {isVerifying ? (
                <div className="flex justify-center">
                  <BeatLoader color="#fff" />
                </div>
              ) : (
                "Verify & Log in"
              )}
            </button>
          </form>

          <div className="flex justify-between items-center text-sm">
            <button
              type="button"
              disabled={isResending}
              onClick={handleResend}
              className="text-primaryBlue font-medium disabled:opacity-50"
            >
              {isResending ? "Resending..." : "Resend code"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("credentials");
                setPassword("");
                setOtp("");
              }}
              className="text-gray400 font-medium"
            >
              Change account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={requestOtp}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="w-full grid place-content-center bg-white">
            <div className="flex  flex-col  w-full  overflow-hidden">
              <div className="mb-6">
                <p className="text-gray600 text-[28px] sm:text-3xl font-bold mb-2">
                  Log In
                </p>
                <p className="text-gray400 font-medium text-sm">
                  Choose your password to log into your account
                </p>
              </div>
            </div>
            <div className="mb-4">

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="email"
                  className="mb-1 text-sm font-medium text-gray600"
                >
                  Email Address
                </label>
                <div className="flex relative">
                  <Field
                    type={"email"}
                    id="email"
                    name="email"
                    className="input-style bg-primaryGray"
                    placeholder="Enter your email"
                  />
                </div>
                <ErrorMessage
                  name="email"
                  component="p"
                  className="error-style"
                />
              </div>

              <div className="flex mt-5 flex-col gap-2">
                <label
                  htmlFor="password"
                  className="mb-1 text-sm font-medium text-gray600"
                >
                  Password
                </label>
                <div className="flex relative">
                  <Field
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="input-style bg-primaryGray"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    aria-label="Toggle password visibility"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-3 text-gray-500 cursor-pointer focus:outline-none"
                  >
                    {showPassword ? (
                      <AiOutlineEye />
                    ) : (
                      <AiOutlineEyeInvisible />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="password"
                  component="p"
                  className="error-style"
                />
              </div>
            </div>
            <div className="">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-primaryBlue hover:opacity-50 text-white w-full mb-4  rounded-md cursor-pointer font-bold py-3 px-6"
              >
                {isSubmitting ? (
                  <div className="flex justify-center">
                    <BeatLoader color="#fff" />
                  </div>
                ) : (
                  "Log in"
                )}
              </button>
            </div>

            <div className="flex flex-col text-center   mt-4 w-full">
              <div className="text-sm text-[#8E8E8E]">
                By signing in, you have agreed to our
              </div>
              <Link to="#" className="ml-2 font-bold text-sm  ">
                <span className="text-primaryBlue text-sm font-medium">
                  Terms <span className="text-gray400">and</span> conditions
                </span>
              </Link>
            </div>
            <br />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
